import { useSuspenseQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React from 'react';

import type { UsersColumnDefinition } from '@gitbook/doc-core';
import {
    createPalette,
    PaletteCommandType,
    type PaletteDef,
    type PaletteGroupDef,
    PalettePositionWidth,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';

import { apiQuery } from '../../../../state';
import { useContentReferenceContext } from '../../../ContentReferences';
import { searchOrganizationMembers } from '../../../Search';
import { UserAvatar } from '../../../Users';
import { InsertValueButton } from './buttons';
import type {
    RecordValueRenderProps,
    TableDefinitionCommandsParams,
    TableDefinitionRenderSpec,
} from './types';

export const usersDefinitionSpec: TableDefinitionRenderSpec<string[], UsersColumnDefinition> = {
    title: 'Users',
    description: 'A user field lets you add collaborators to your records.',
    icon: Icon.Users,
    ValueComponent: RecordUsersValue,
    commands: computeUsersColumnCommands,
    onKeyDown: (key, value, setValue) => {
        switch (key) {
            case 'backspace':
                if (value.length > 0) {
                    setValue(value.slice(0, -1));
                }
                break;
        }
    },
};

function computeUsersColumnCommands(
    params: TableDefinitionCommandsParams<UsersColumnDefinition>
): PaletteGroupDef[] {
    const { definition, setDefinition } = params;

    return [
        {
            key: 'users',
            commands: [
                {
                    key: 'users.allow_multiple',
                    title: 'Allow multiple',
                    icon: Icon.Team,
                    type: PaletteCommandType.SWITCH,
                    enabled: definition.multiple,
                    trigger: () => {
                        const [palette] = setDefinition({
                            ...definition,
                            multiple: !definition.multiple,
                        });
                        return palette;
                    },
                },
            ],
        },
    ];
}

/**
 * Cell content for a Users column.
 */
function RecordUsersValue(
    props: RecordValueRenderProps<string[], UsersColumnDefinition>
): React.ReactElement {
    const { value, selected, readOnly, displayMode, definition, onValueChange, onKeyDown } = props;
    const addButtonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(addButtonRef, {
        onClose: () => {
            displayMode === 'cell' && addButtonRef.current?.focus();
        },
    });

    const contentReferenceContext = useContentReferenceContext();

    React.useEffect(() => {
        // If the current cell is `selected`, focus the + button
        selected ? addButtonRef.current?.focus() : addButtonRef.current?.blur();
    }, [selected]);

    /** Remove a user from the value */
    const onRemove = (index: number) => {
        const newValue = [...value];
        newValue.splice(index, 1);
        onValueChange(newValue);
    };

    const usersSelectionPalette = createUsersSelectionPalette(
        definition,
        value,
        onValueChange,
        contentReferenceContext.organization
    );

    return (
        <div className="relative flex flex-1">
            <div className="flex flex-1 flex-wrap gap-3">
                <React.Suspense fallback={null}>
                    {value.map((userId, index) => (
                        <UserItem
                            key={userId}
                            userId={userId}
                            readOnly={readOnly}
                            onRemove={() => onRemove(index)}
                        />
                    ))}
                </React.Suspense>
            </div>
            {/* Only show the controls when this RecordValue is used in the modal. In cards + table, we show controls in a different way. */}
            {displayMode === 'modal' && !readOnly ? (
                <InsertValueButton
                    buttonRef={addButtonRef}
                    leadingIcon={value.length > 0 ? Icon.Pencil : Icon.Plus}
                    active={paletteHandle.opened}
                    label="Add user"
                    onKeyDown={onKeyDown}
                    onPress={() => paletteHandle.open(usersSelectionPalette)}
                    tooltip={{
                        label: 'Add user',
                        shortcut: 'space',
                    }}
                />
            ) : null}
        </div>
    );
}

/**
 * Creates the users selection palette - extracted for reuse in unified cell actions
 */
export function createUsersSelectionPalette(
    definition: UsersColumnDefinition,
    value: string[],
    onValueChange: (value: string[]) => void,
    organization: string
): PaletteDef {
    const handleToggleUser = (userId: string) => {
        if (definition.multiple) {
            onValueChange(
                value.includes(userId) ? value.filter((id) => id !== userId) : [...value, userId]
            );
        } else {
            onValueChange([userId]);
        }
    };

    return createPalette({
        search: { placeholder: 'Search users…' },
        position: { width: PalettePositionWidth.SMALL },
        groups: [
            {
                key: 'users',
                commands: (query) => {
                    const members = searchOrganizationMembers({
                        organizationId: organization,
                        query,
                    });

                    return members.map((member) => ({
                        key: member.user.id,
                        title: member.user.displayName,
                        icon: () => <UserAvatar user={member.user} size="xs" />,
                        selected: value.includes(member.user.id),
                        type: definition.multiple
                            ? PaletteCommandType.CHECKBOX
                            : PaletteCommandType.RADIO,
                        trigger: () => {
                            handleToggleUser(member.user.id);
                        },
                    }));
                },
            },
        ],
    });
}

/** Item for a user in the table */
function UserItem(props: {
    userId: string;
    readOnly: boolean;
    onRemove: () => void;
}): React.ReactElement {
    const { userId, readOnly, onRemove } = props;
    const viewRef = React.useRef<HTMLDivElement>(null);
    const paletteHandle = usePaletteHandle(viewRef);

    const { data: user } = useSuspenseQuery(
        apiQuery('getUserById', { userId }, { nullOnError: true })
    );

    const onPress = () => {
        paletteHandle.open(
            createPalette({
                position: { width: PalettePositionWidth.SMALL },
                groups: [
                    {
                        key: 'user',
                        commands: [
                            {
                                key: 'remove',
                                title: 'Remove',
                                icon: Icon.Trashcan,
                                trigger: () => {
                                    onRemove();
                                },
                            },
                        ],
                    },
                ],
            })
        );
    };

    return (
        <div
            ref={viewRef}
            onClick={readOnly ? undefined : onPress}
            className={clsx(
                'flex items-center gap-1',
                !readOnly && 'cursor-pointer hover:text-primary'
            )}
        >
            <UserAvatar user={user} size="xs" />
            <span className="truncate">{user ? user.displayName : 'Deleted'}</span>
        </div>
    );
}
